import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  storeOfflineBook,
  removeOfflineBook,
  getOfflineBookManifest,
  getOfflineManifest,
} from '../utils/offlineBibleStore';
import { getBookName } from '../utils/bookTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-tap "Save book" control shown in the reader. Downloads every chapter of
 * the current book+translation into IndexedDB so chapters open instantly with
 * no spinner — even on a weak/flaky connection. If the whole translation is
 * already downloaded, the book is implicitly offline and shows a ready badge.
 */
const SaveBookOfflineButton = ({ book, translation, language = 'en' }) => {
  // idle | saved | fullsaved | downloading
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);

  const check = useCallback(async () => {
    try {
      const [full, bookM] = await Promise.all([
        getOfflineManifest(translation),
        getOfflineBookManifest(translation, book),
      ]);
      if (full) setStatus('fullsaved');
      else if (bookM) setStatus('saved');
      else setStatus('idle');
    } catch {
      setStatus('idle');
    }
  }, [book, translation]);

  useEffect(() => { check(); }, [check]);

  const handleSave = async () => {
    setStatus('downloading');
    setProgress(5);
    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
          'GET',
          `${BACKEND_URL}/api/bible/book?book=${encodeURIComponent(book)}&translation=${encodeURIComponent(translation)}`
        );
        xhr.responseType = 'json';
        xhr.onprogress = (e) => {
          const pct = e.lengthComputable ? Math.round((e.loaded / e.total) * 70) : null;
          setProgress((p) => pct ?? Math.min(65, p + 6));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve(xhr.response)
            : reject(new Error(`HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });

      const verses = (data && data.verses) || [];
      if (!verses.length) throw new Error('empty');
      setProgress(82);
      await storeOfflineBook(translation, book, verses, data.total_chapters);
      setProgress(100);
      setStatus('saved');
      const name = getBookName(book, translation, language);
      toast.success(
        language === 'es'
          ? `${name} guardado sin conexión (${verses.length.toLocaleString()} versículos)`
          : `${name} saved for offline reading (${verses.length.toLocaleString()} verses)`
      );
    } catch (e) {
      setStatus('idle');
      toast.error(
        language === 'es'
          ? 'No se pudo guardar este libro sin conexión'
          : 'Could not save this book offline'
      );
    }
  };

  const handleRemove = async () => {
    await removeOfflineBook(translation, book);
    setStatus('idle');
    toast.info(
      language === 'es' ? 'Libro eliminado del dispositivo' : 'Book removed from device'
    );
  };

  if (status === 'fullsaved') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
        title={language === 'es' ? 'Disponible sin conexión' : 'Available offline'}
        data-testid="save-book-fullsaved"
      >
        <Check className="w-3 h-3" />
        {language === 'es' ? 'Sin conexión' : 'Offline ready'}
      </span>
    );
  }

  if (status === 'saved') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-[11px] text-emerald-500 hover:text-emerald-600"
        onClick={handleRemove}
        data-testid="save-book-remove-btn"
        title={language === 'es' ? 'Quitar libro sin conexión' : 'Remove offline book'}
      >
        <Check className="w-3.5 h-3.5 mr-1" />
        {language === 'es' ? 'Guardado' : 'Saved'}
      </Button>
    );
  }

  if (status === 'downloading') {
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-[11px]"
        disabled
        data-testid="save-book-progress"
      >
        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
        {progress}%
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 px-2 text-[11px]"
      onClick={handleSave}
      data-testid="save-book-offline-btn"
      title={
        language === 'es'
          ? 'Guardar este libro para leer sin conexión'
          : 'Save this book for instant, offline reading'
      }
    >
      <Download className="w-3.5 h-3.5 mr-1" />
      {language === 'es' ? 'Guardar libro' : 'Save book'}
    </Button>
  );
};

export default SaveBookOfflineButton;
